import type { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useMutation, useQuery, useSubscription } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import { closeSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  DialogID,
  subscribeToDialogs,
  useDialogStore,
  useDialogUnsynced,
} from '@components/Dialog';
import { AppointmentErrorCodes } from '@constants/apiErrors';
import { FeatureFlags } from '@constants/featureFlags';
import { AppointmentErrorDialog } from '@features/Remote/AppointmentErrorDialog';
import { AppointmentSnackbar } from '@features/Remote/constants';
import {
  Appointment,
  AppointmentParticipantType,
  AppointmentType,
  ControlGivenEvent,
  ControlRequestedEvent,
} from '@generated/gql/graphql';
import { useFeatureFlag } from '@hooks/useFeatureFlag';
import { SnackbarVariant, useSnackbar } from '@hooks/useSnackbar';
import { useRequestStates } from '@hooks/useSyncedRequest';
import { EventTypes, emitter } from '@services/emitter';
import {
  useAppointmentId,
  useAppointmentUserId,
  useAppointmentUserType,
  useSetAppointmentSession,
} from '@store/appointment/selectors';
import { JOURNEY_MODE, useMode } from '@store/journey';
import { useTrackingContext } from '@tracking/Provider/Context';
import { useTrackSignupRemoteAppointmentEnded } from '@tracking/trackingEvents';
import {
  appointmentInteractionDialog,
  appointmentUpdated,
  endRemoteAppointment,
  appointment as getAppointment,
} from '@utils/queries/appointment';
import {
  clearFormPersistStorage,
  removeAppointmentFromLocalStorage,
} from '@utils/storagePropertyManagement';
import { useBoundStore } from '~/src/store';

import { useBasketContext } from '../basket/BasketProvider/Context';
import { useDetailsContext } from '../basket/DetailsProvider/Context';
import { AppointmentContext } from './AppointmentContext';
import { AppointmentControlContext } from './AppointmentControlContext';
import { SyncedRequestContext } from './SyncedRequestContext';
import type {
  AppointmentState,
  Session,
  SessionContextProvider,
} from './types';
import { useAppointmentEventHandler } from './useAppointmentEventHandler';
import { useInteractionEventHandler } from './useInteractionEventHandler';

const useAppointmentDataSync = (
  subData: { appointmentUpdated?: Nullable<Appointment> } | undefined,
  appointmentQueryData: { appointment?: Nullable<Appointment> } | undefined,
  queryLoading: boolean,
  setAppointment: (appointment: Nullable<Appointment>) => void,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const userId = useAppointmentUserId();
  const userType = useAppointmentUserType();
  useEffect(() => {
    if (subData?.appointmentUpdated) {
      setAppointment(subData.appointmentUpdated);
    } else if (appointmentQueryData?.appointment) {
      // set the appointment ID sentry tag when a user joins or
      // rejoins a remote session
      Sentry.setTag('appointment_id', appointmentQueryData.appointment.id);
      Sentry.setTag('appointment_participant_type', userType);
      Sentry.setTag('appointment_participant_id', userId);
      setAppointment(appointmentQueryData.appointment);
    }

    // we set this here so that our loading state is updated in the same render
    // as we update the appointment state, otherwise we can have
    // loading = false, appointment = null
    setLoading(queryLoading);
  }, [
    appointmentQueryData,
    subData?.appointmentUpdated,
    queryLoading,
    setLoading,
    setAppointment,
    userId,
    userType,
  ]);
};

const useAppointmentBasketSync = (
  appointment: Nullable<Appointment>,
  basketId: string | null,
  setBasketId: (id: string) => void
) => {
  useEffect(() => {
    if (appointment?.basketId && appointment.basketId !== basketId) {
      setBasketId(appointment.basketId);
    }
  }, [appointment?.basketId, basketId, setBasketId]);
};

const useAppointmentDialogSync = (
  appointmentId: Nullable<string>,
  hasControl: Nullable<boolean>,
  dialogInteraction: (options: {
    variables: {
      id: string;
      dialog: string;
      open: boolean;
      isLoading: boolean;
      variant?: Nullable<string>;
    };
  }) => void
) => {
  useEffect(() => {
    if (appointmentId && hasControl) {
      const unsubscribe = subscribeToDialogs((id, state) => {
        dialogInteraction({
          variables: {
            id: appointmentId,
            dialog: id,
            open: state.open,
            isLoading: state.isLoading,
            variant: state.variant,
          },
        });
      });

      return unsubscribe;
    }
  }, [appointmentId, hasControl, dialogInteraction]);
};

const useAppointmentErrorHandler = (
  queryError: ErrorLike | undefined,
  subError: ErrorLike | undefined,
  errorDialog: ReturnType<typeof useDialogUnsynced>,
  end: (callMutation?: boolean) => Promise<void>,
  snackbar: ReturnType<typeof useSnackbar>
) => {
  useEffect(() => {
    if (queryError || subError) {
      const error = queryError || subError;
      const errorCode = CombinedGraphQLErrors.is(error)
        ? error.errors[0]?.extensions?.['code']
        : undefined;
      switch (errorCode) {
        case AppointmentErrorCodes.appointmentNotFound:
        case AppointmentErrorCodes.appointmentAlreadyEnded:
          end(false);
          return;
        case AppointmentErrorCodes.appointmentNotMember:
          end(false);
          snackbar({
            message:
              'You have disconnected from the appointment. Your partner can share the appointment details with you again if you wish to rejoin',
            variant: SnackbarVariant.Error,
            preventDuplicate: true,
            persist: true,
          });
          return;
      }
      errorDialog.setOpen(true);
    }
  }, [queryError, subError, errorDialog, end, snackbar]);
};

const useAppointmentSnackbarCleanup = (appointment: Nullable<Appointment>) => {
  useEffect(() => {
    if (appointment?.isEnded) {
      closeSnackbar();
    }
  }, [appointment?.isEnded]);
};

/**
 * update tracking context when appointment updates
 */
const useAppointmentTrackingSync = (
  appointment: Nullable<Appointment>,
  session: { userType?: string | null },
  setSignupRemoteAppointmentContext: (ctx: {
    control: string;
    remote_actor: string;
    appointment_id: string;
  }) => void
) => {
  useEffect(() => {
    setSignupRemoteAppointmentContext({
      control: appointment?.controller.type ?? '',
      remote_actor: session.userType ?? '',
      appointment_id: appointment?.id ?? '',
    });
  }, [
    appointment?.controller.type,
    appointment?.id,
    session?.userType,
    setSignupRemoteAppointmentContext,
  ]);
};

const AppointmentProvider: React.FC<SessionContextProvider> = ({
  children,
}) => {
  const appointment = useBoundStore((state) => state.appointment);
  const setAppointment = useBoundStore((state) => state.setAppointment);
  const snackbar = useSnackbar();
  const [controlRequest, setControlRequest] =
    useState<Nullable<ControlRequestedEvent>>(null);
  const [controlGiven, setControlGiven] =
    useState<Nullable<ControlGivenEvent>>(null);
  const [awaitingRemoteSupport, setAwaitingRemoteSupport] =
    useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const appointmentId = useAppointmentId();
  const userId = useAppointmentUserId();
  const userType = useAppointmentUserType();
  const setAppointmentSession = useSetAppointmentSession();
  const isUserEndingRef = useRef(false);
  const { basketId, setBasketId, handleStartAgain } = useBasketContext();
  const { resetDetails } = useDetailsContext();
  const dialogs = useDialogStore();
  const errorDialog = useDialogUnsynced(DialogID.APMT_ERROR);
  const { setLoading: setRequestLoading, requestStates } = useRequestStates();

  const { value: isRsScrollSyncEnabled } = useFeatureFlag(
    FeatureFlags.RS_SCROLL_SYNC,
    false
  );

  const session: Session = useMemo(
    () => ({ appointmentId, userId, userType }),
    [appointmentId, userId, userType]
  );

  const mode = useMode();
  const isInLifeMode = mode === JOURNEY_MODE.INLIFE;

  const isSyncedAppointment =
    !isInLifeMode &&
    !!appointment &&
    (appointment.type === AppointmentType.PartnerRemote ||
      appointment.type === AppointmentType.FaceToFaceWithRemoteSupport ||
      appointment.type === AppointmentType.PartnerRemoteWithFaceToFaceSupport);

  const hasControl = appointment
    ? isSyncedAppointment
      ? appointment.controller?.id === userId
      : null
    : null;
  // Whether is allowed to update and push state changes.
  // True if there is no remote appointment or face to face appointment
  // or if this user has control during a remote appointment or face to face with remote support
  const canUpdateState =
    !isSyncedAppointment || (isSyncedAppointment && !!hasControl);

  const [dialogInteraction] = useMutation(appointmentInteractionDialog);
  const [endAppointment] = useMutation(endRemoteAppointment);

  const trackAppointmentEnded = useTrackSignupRemoteAppointmentEnded();
  const { trackingContexts, setSignupRemoteAppointmentContext } =
    useTrackingContext();

  const {
    data: appointmentQueryData,
    loading: queryLoading,
    error: queryError,
  } = useQuery(getAppointment, {
    variables: {
      id: appointmentId ?? '',
    },
    skip: !appointmentId,
  });

  const { data: subData, error: subError } = useSubscription(
    appointmentUpdated,
    {
      variables: {
        id: appointmentId ?? '',
      },
      skip: !appointmentId,
      onData: ({ data }) => {
        const event = data.data?.appointmentUpdated;
        if (!event) return;
        if (!appointment?.path || appointment.path === event.path) return;
        if (event.path && appointment?.path !== event.path) {
          closeSnackbar(AppointmentSnackbar.CUSTOMER_JOINED);
          closeSnackbar(AppointmentSnackbar.CUSTOMER_LEFT);
        }
        if (userId !== event.controller.id) {
          emitter.emit(EventTypes.RemoteNavigation, event.path);
        }
      },
    }
  );

  const clearAppointment = useCallback(() => {
    setAppointment(null);
    setControlGiven(null);
    removeAppointmentFromLocalStorage();
    setAppointmentSession({
      appointmentId: null,
    });
  }, [setAppointmentSession, setAppointment]);

  const clearStorage = useCallback(
    (removeHostedAttribution = true) => {
      clearAppointment();
      resetDetails();
      clearFormPersistStorage();
      handleStartAgain({ removeHostedAttribution });
    },
    [clearAppointment, resetDetails, handleStartAgain]
  );

  const end = useCallback(
    async (callMutation = true, { removeHostedAttribution = true } = {}) => {
      isUserEndingRef.current = true;

      if (appointmentId && callMutation) {
        await endAppointment({
          variables: {
            id: appointmentId,
          },
        });
      }
      clearStorage(removeHostedAttribution);
      trackAppointmentEnded({}, { contexts: trackingContexts });

      isUserEndingRef.current = false;
    },
    [
      appointmentId,
      clearStorage,
      trackAppointmentEnded,
      trackingContexts,
      endAppointment,
    ]
  );

  useEffect(() => {
    if (appointment?.isEnded && !isUserEndingRef.current) {
      end(false);
    }
  }, [appointment?.isEnded, end]);

  useInteractionEventHandler({
    appointmentId,
    userId,
    appointment,
    hasControl,
    setRequestLoading,
    dialogs,
    isRsScrollSyncEnabled,
  });

  useAppointmentEventHandler({
    appointmentId,
    userId,
    appointment,
    controlRequest,
    setControlGiven,
    setControlRequest,
    snackbar,
    clearStorage,
  });

  useAppointmentDataSync(
    subData,
    appointmentQueryData,
    queryLoading,
    setAppointment,
    setLoading
  );
  useAppointmentBasketSync(appointment, basketId, setBasketId);
  useAppointmentDialogSync(appointmentId, hasControl, dialogInteraction);
  useAppointmentErrorHandler(queryError, subError, errorDialog, end, snackbar);
  useAppointmentSnackbarCleanup(appointment);
  useAppointmentTrackingSync(
    appointment,
    session,
    setSignupRemoteAppointmentContext
  );

  const hasParticipantType = (type: AppointmentParticipantType) =>
    appointment?.participants?.some(
      (participant) => participant.type === type
    ) ?? false;

  const hasCustomer = hasParticipantType(AppointmentParticipantType.Customer);
  const hasSupportingPartner = hasParticipantType(
    AppointmentParticipantType.SupportingPartner
  );
  const isRemoteSupport =
    appointment?.type === AppointmentType.FaceToFaceWithRemoteSupport;

  const personLabel = isRemoteSupport ? 'supporting Partner' : 'customer';

  const value: AppointmentState = useMemo(
    () => ({
      appointment,
      session,
      loading,
      isSyncedAppointment,
      hasControl,
      canUpdateState,
      endAppointment: end,
      hasCustomer,
      hasSupportingPartner,
      isRemoteSupport,
      personLabel,
      awaitingRemoteSupport,
      setAwaitingRemoteSupport,
    }),
    [
      appointment,
      session,
      loading,
      isSyncedAppointment,
      hasControl,
      canUpdateState,
      end,
      hasCustomer,
      hasSupportingPartner,
      isRemoteSupport,
      personLabel,
      awaitingRemoteSupport,
      setAwaitingRemoteSupport,
    ]
  );

  const controlValue = useMemo(
    () => ({ controlRequest, controlGiven }),
    [controlRequest, controlGiven]
  );

  const syncedRequestValue = useMemo(
    () => ({ syncedRequestStates: requestStates }),
    [requestStates]
  );

  return (
    <AppointmentContext value={value}>
      <AppointmentControlContext value={controlValue}>
        <SyncedRequestContext value={syncedRequestValue}>
          {children}
          <AppointmentErrorDialog />
        </SyncedRequestContext>
      </AppointmentControlContext>
    </AppointmentContext>
  );
};

export { AppointmentProvider };
